/**
 * Single source of truth for turning the backend's generic
 * "service + category attributes + variants" payload into the event shape the
 * storefront renders. Every page used to re-parse attributes by hand (and
 * disagree slightly); they all go through here now.
 */
import { addDays, format, isValid, parseISO } from 'date-fns';
import { cleanLocationText } from '@/utils/cleanLocationText';

/* ------------------------------------------------------------------ parsing */

export const parseMaybeJSON = (value) => {
  if (value && typeof value === 'object') return value;
  if (typeof value !== 'string') return {};
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

/** Attributes come back as `[{ value, categoryAttribute: { attributeKey } }]`. */
export const attributeMap = (attributes = []) => {
  const map = {};
  attributes.forEach((attr) => {
    const key = attr?.categoryAttribute?.attributeKey;
    if (key) map[key] = attr.value;
  });
  return map;
};

/** The admin panel stores event dates as `d-m-yyyy` (no zero padding). */
export const parseAdminDate = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  const dashed = trimmed.split('-').map(Number);
  if (dashed.length === 3 && dashed.every((n) => !Number.isNaN(n))) {
    const [day, month, year] = dashed;
    // A yyyy-mm-dd string lands here too — detect it by the 4-digit head.
    if (String(trimmed.split('-')[0]).length === 4) {
      const iso = parseISO(trimmed);
      return isValid(iso) ? iso : null;
    }
    const date = new Date(year, month - 1, day);
    return isValid(date) ? date : null;
  }
  const fallback = new Date(trimmed);
  return isValid(fallback) ? fallback : null;
};

/**
 * `event_date` is a `date_range` attribute: "22-9-2026,24-9-2026". Expand it to
 * every bookable day so the date strip can render one chip per day.
 */
export const expandDateRange = (value, cap = 60) => {
  if (!value) return [];
  const [startRaw, endRaw] = String(value).split(',').map((s) => s.trim());
  const start = parseAdminDate(startRaw);
  const end = parseAdminDate(endRaw) || start;
  if (!start || !end) return [];
  const days = [];
  let cursor = start;
  while (cursor <= end && days.length < cap) {
    days.push(format(cursor, 'yyyy-MM-dd'));
    cursor = addDays(cursor, 1);
  }
  return days;
};

/** `event_time` is a `time_range` attribute: "7:00 pm,8:00 pm". */
export const parseTimeRange = (value) =>
  String(value || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);

/** `multiple_file` attributes are a comma-joined URL list. */
export const parseFileList = (value) =>
  String(value || '')
    .split(',')
    .map((u) => u.trim())
    .filter((u) => /^https?:\/\//i.test(u));

export const parseMultiselect = (value) =>
  String(value || '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

/* ------------------------------------------------------------------ helpers */

const toRad = (deg) => (deg * Math.PI) / 180;

export const distanceKm = (lat1, lon1, lat2, lon2) => {
  const nums = [lat1, lon1, lat2, lon2].map(Number);
  if (nums.some((n) => Number.isNaN(n) || n === 0)) return null;
  const [a1, o1, a2, o2] = nums;
  const R = 6371;
  const dLat = toRad(a2 - a1);
  const dLon = toRad(o2 - o1);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a1)) * Math.cos(toRad(a2)) * Math.sin(dLon / 2) ** 2;
  return +(R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))).toFixed(1);
};

/** The location the visitor picked, falling back to Bikaner (LK's home city). */
export const readStoredLocation = () => {
  try {
    const raw = localStorage.getItem('location');
    const parsed = raw ? JSON.parse(raw) : null;
    if (parsed?.lat && parsed?.long) {
      return {
        lat: Number(parsed.lat),
        lng: Number(parsed.long),
        name: parsed.name,
        zoneId: parsed.zoneId != null ? Number(parsed.zoneId) : null,
      };
    }
  } catch {
    /* fall through to the default */
  }
  return { lat: 28.031097, lng: 73.319387, name: 'Bikaner', zoneId: null };
};

export const formatINR = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
};

export const formatDayLabel = (isoDay) => {
  const d = parseISO(isoDay);
  return isValid(d) ? format(d, 'EEE, d MMM') : isoDay;
};

/** "Wed, 26 Aug" / "26 Aug – 31 Aug" depending on how long the event runs. */
export const formatDateSpan = (days = []) => {
  if (days.length === 0) return 'Date TBA';
  const first = parseISO(days[0]);
  const last = parseISO(days[days.length - 1]);
  if (!isValid(first)) return 'Date TBA';
  if (days.length === 1) return format(first, 'EEE, d MMM');
  const sameMonth = format(first, 'MMM') === format(last, 'MMM');
  return sameMonth
    ? `${format(first, 'd')} – ${format(last, 'd MMM')}`
    : `${format(first, 'd MMM')} – ${format(last, 'd MMM')}`;
};

/* ------------------------------------------------------------- normalisation */

const normalizeVariant = (variant) => {
  const price = Number(variant?.price ?? 0);
  const sale = variant?.salePrice != null ? Number(variant.salePrice) : null;
  const effective = sale && sale > 0 ? sale : price;
  const qty = Number(variant?.qty ?? 0);
  const sold = Number(variant?.soldQty ?? 0);
  const left = Math.max(qty - sold, 0);
  const soldOut = variant?.status === 'sold' || left <= 0;
  // Set by hand in admin: highlight the tier, optionally with a typed-in count.
  const fillingFast = !!variant?.fillingFast && variant?.status === 'active' && !soldOut;
  const shownLeft = Number(variant?.ticketsLeftDisplay ?? 0);
  return {
    id: String(variant?.id),
    name: variant?.name || 'Ticket',
    description: variant?.description || '',
    price: effective,
    mrp: sale && sale > 0 && price > sale ? price : null,
    discountPct: sale && sale > 0 && price > sale ? Math.round(((price - sale) / price) * 100) : 0,
    qty,
    sold,
    available: left,
    color: variant?.color || null,
    displayOrder: Number(variant?.displayOrder ?? 0),
    soldOut,
    status: variant?.status,
    fillingFast,
    fillingFastLeft: fillingFast && shownLeft > 0 ? shownLeft : null,
    /**
     * Multi-day events: the day this ticket is for. Null = on sale every day,
     * which is also how a multi-day pass is expressed.
     */
    eventDay: variant?.eventDay ? String(variant.eventDay).slice(0, 10) : null,
  };
};

/** The tickets on sale on `day` — a ticket with no day is on sale every day. */
export const variantsForDay = (variants = [], day) =>
  variants.filter((v) => !v.eventDay || !day || v.eventDay === day);

export const normalizeProvider = (provider, origin) => {
  if (!provider) return null;
  return {
    id: provider.id,
    name: provider.name || 'Venue',
    description: provider.description || '',
    logo: provider.logo || null,
    cover: provider.cover || provider.logo || null,
    address: cleanLocationText(provider.address) || '',
    phone: provider.phone || null,
    email: provider.email || null,
    rating: Number(provider.rating ?? 0),
    totalReviews: Number(provider.totalReviews ?? 0),
    latitude: Number(provider.latitude) || null,
    longitude: Number(provider.longitude) || null,
    isActive: !!provider.isActive,
    categoryId: provider.categoryId,
    categoryName: provider.category?.name || '',
    // The city zone this organiser sits in (admin-drawn polygon). null = not
    // mapped yet; such events are never hidden by the city filter.
    zoneId: provider.zoneId ?? null,
    distanceKm: origin
      ? distanceKm(origin.lat, origin.lng, provider.latitude, provider.longitude)
      : null,
  };
};

/**
 * Turn one `/services` row into the storefront event model.
 * `origin` is `{ lat, lng }` and drives the "x km away" label.
 */
export const normalizeService = (service, origin = readStoredLocation()) => {
  if (!service) return null;
  const attrs = attributeMap(service.attributes);

  const days = expandDateRange(attrs.event_date);
  const times = parseTimeRange(attrs.event_time);
  const location = parseMaybeJSON(attrs.event_location);
  const provider = normalizeProvider(service.provider, origin);

  const gallery = [
    ...parseFileList(attrs.event_files),
    ...parseFileList(attrs.images_event),
  ].filter((url, i, all) => all.indexOf(url) === i);

  const variants = (service.variants || [])
    .map(normalizeVariant)
    .sort((a, b) => a.displayOrder - b.displayOrder || a.price - b.price);

  const sellable = variants.filter((v) => !v.soldOut);
  const prices = (sellable.length ? sellable : variants).map((v) => v.price);
  const priceFrom = prices.length ? Math.min(...prices) : Number(service.price || 0);
  const priceTo = prices.length ? Math.max(...prices) : priceFrom;
  const bestDiscount = variants.reduce((max, v) => Math.max(max, v.discountPct), 0);

  const capacity = variants.reduce((sum, v) => sum + v.qty, 0);
  const sold = variants.reduce((sum, v) => sum + v.sold, 0);

  // Only trust the event_location coordinates when that attribute also carries
  // an address — otherwise the pin and the address label would describe two
  // different places, and the "x km away" figure would contradict the venue.
  const hasLocationAddress = !!cleanLocationText(location?.address);
  const venueLat = (hasLocationAddress && Number(location?.latitude)) || provider?.latitude || null;
  const venueLng = (hasLocationAddress && Number(location?.longitude)) || provider?.longitude || null;

  const thumbnail = service.thumbnail || gallery[0] || provider?.cover || '';

  return {
    id: String(service.id),
    title: service.name || 'Event',
    description: attrs.event_description || service.description || '',
    thumbnail,
    gallery: gallery.length ? gallery : [thumbnail].filter(Boolean),

    categoryId: service.categoryId,
    category: service.category || null,
    categoryName: service.category?.name || '',
    /**
     * Admin-configured browse buckets this event is filed under. Real rows,
     * unlike `eventTypes` below — which reads a *provider*-entity attribute
     * that never appears in a service payload, so it has always been empty.
     */
    subcategories: (service.subcategories ?? []).map((sub) => ({
      id: sub.id,
      name: sub.name,
      slug: sub.slug,
    })),
    // `event_type` is an admin-managed multiselect — it powers the quick chips.
    eventTypes: parseMultiselect(attrs.event_type),
    // The lineup, headliner first (admin/organiser-set). Links to /artist/<slug>.
    artists: (service.artists ?? []).map((a) => ({
      id: a.id,
      name: a.name,
      slug: a.slug,
      photo: a.photo || null,
      tagline: a.tagline || null,
    })),

    providerId: service.providerId,
    provider,
    // The city the event happens in: its own (set in LK Admin) when it differs
    // from the organiser's, else the organiser's.
    zoneId: service.zoneId ?? provider?.zoneId ?? null,

    days,
    startDate: days[0] || null,
    endDate: days[days.length - 1] || null,
    dateLabel: formatDateSpan(days),
    times,
    timeLabel: times.length > 1 ? `${times[0]} – ${times[1]}` : times[0] || '',

    location: {
      address: cleanLocationText(location?.address) || provider?.address || '',
      city: cleanLocationText(location?.city) || '',
      latitude: venueLat,
      longitude: venueLng,
    },
    venueName: provider?.name || cleanLocationText(location?.address) || 'Venue',
    distanceKm: origin ? distanceKm(origin.lat, origin.lng, venueLat, venueLng) : null,

    variants,
    priceFrom,
    priceTo,
    isFree: priceFrom === 0,
    discountPct: bestDiscount,
    capacity,
    sold,
    ticketsLeft: Math.max(capacity - sold, 0),
    soldOut: variants.length > 0 && variants.every((v) => v.soldOut),
    // Below ~15% remaining we surface urgency on the card.
    lowStock: capacity > 0 && capacity - sold > 0 && (capacity - sold) / capacity <= 0.15,
    // Admin marked a tier "Filling fast"; the smallest typed-in count, if any.
    fillingFast: variants.some((v) => v.fillingFast),
    fillingFastLeft: variants.reduce(
      (min, v) => (v.fillingFastLeft != null && (min == null || v.fillingFastLeft < min) ? v.fillingFastLeft : min),
      null
    ),

    duration: Number(service.duration) || null,
    isActive: !!service.isActive,
    // Announced but not on sale. Still listed — the storefront renders a
    // teaser and hides every buy path. Backend rejects quotes/bookings for
    // these too, so this is presentation, not the enforcement.
    comingSoon: !!service.isComingSoon,
    approvalStatus: service.approvalStatus,
    raw: service,
  };
};

/**
 * Listed events — mirrors what the admin panel toggles control. Coming-soon
 * events pass: they're meant to be seen, just not bought. Use `isBookable`
 * before showing any buy affordance.
 */
export const isPubliclyVisible = (event) =>
  !!event &&
  event.isActive &&
  event.approvalStatus === 'approved' &&
  !!event.provider?.isActive;

/** Visible AND on sale — the gate for every ticket/checkout entry point. */
export const isBookable = (event) => isPubliclyVisible(event) && !event.comingSoon;

export const humanDuration = (minutes) => {
  if (!minutes) return '';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
};

/**
 * Remember the city a visitor picked. Written under the key readStoredLocation
 * already reads, so distances ("x km away") are measured from the chosen city.
 * The old picker only changed the header label and never saved coordinates,
 * so every distance was secretly from Bikaner.
 */
export const storeCity = (city) => {
  try {
    localStorage.setItem(
      'location',
      JSON.stringify({ name: city.name, lat: city.lat, long: city.lng, zoneId: city.zoneId })
    );
  } catch {
    /* storage unavailable — the choice still applies for this visit */
  }
};
